'use client'

import SubCategoriesGridSkeleton from '@/components/assets-explorer/subcategories-grid-skeleton'
import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { Category, SubCategory } from '@/lib/categories/categories'
import { cn } from '@/lib/utils'
import styles from '@/components/assets-explorer/assets-explorer.module.scss'
import { AssetCard } from '@/components/assets-explorer/asset-card'
import useCountGridColumns from '@/lib/hooks/useCountGridColumns'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import assetCardStyles from '@/components/assets-explorer/asset-card.module.scss'
import HiddenSEOContent from '@/components/hidden-seo-content'
import { useHover } from 'ahooks'
import useHoverOthers from '@/lib/hooks/useHoverOthers'

const SubCategoriesList = ({
  mainCategorySlug,
  categorySlug,
  subCategories,
  initiallyVisibleRows,
  className,
  onHover,
}: {
  mainCategorySlug: string
  categorySlug: string
  subCategories: SubCategory[]
  initiallyVisibleRows?: number
  className?: string
  onHover?: (isHovering: boolean) => void
}) => {
  const ref = React.useRef<HTMLUListElement>(null)

  const { count: columnCount } = useCountGridColumns({
    target: ref,
    minWidth: 260,
    gap: 8,
  })

  const visibleSubCategoriesCount = useMemo(() => {
    if (!columnCount || !initiallyVisibleRows) {
      return subCategories.length
    }

    return Math.max(1, Math.min(subCategories.length, columnCount * initiallyVisibleRows) - 1)
  }, [columnCount, initiallyVisibleRows, subCategories.length])

  const expandable = useMemo(
    () => subCategories.length - 1 > visibleSubCategoriesCount,
    [subCategories.length, visibleSubCategoriesCount]
  )

  const visibleSubCategories = useMemo(
    () => subCategories.slice(0, visibleSubCategoriesCount),
    [subCategories, visibleSubCategoriesCount]
  )

  useHover(ref, {
    onChange: (isHovering) => {
      if (onHover) {
        onHover(isHovering)
      }
    },
  })

  // Handle hover state
  const { handleMouseEnter, handleMouseLeave, isHoveringOther } = useHoverOthers()

  return (
    <ul
      ref={ref}
      className={cn(
        'subcategories-list tw-group/list tw-pointer-events-none',
        styles.assetGallery,
        'tw-duration-500 tw-animate-in tw-fade-in',
        className
      )}
      data-slot="subcategories-list"
    >
      {React.Children.toArray(
        visibleSubCategories.map((subCategory, index) => (
          <li
            key={subCategory.slug}
            className={cn(
              `tw-pointer-events-auto tw-relative tw-z-[0] tw-h-40 tw-transition-opacity tw-duration-300 hover:tw-z-[1] hover:!tw-opacity-100`,
              {
                'tw-hidden': index >= visibleSubCategoriesCount,
                'group-hover/list:tw-opacity-65': isHoveringOther(subCategory.slug),
              }
            )}
            onMouseEnter={() => handleMouseEnter(subCategory.slug)}
            onMouseLeave={handleMouseLeave}
          >
            <AssetCard
              className={assetCardStyles.scaleOnHover}
              title={subCategory.name}
              image={subCategory.image}
              link={`/${subCategory.mainCategorySlug}/${subCategory.categorySlug}/${subCategory.slug}`}
            />
          </li>
        ))
      )}
      {expandable && (
        <li
          className="tw-pointer-events-auto tw-flex tw-place-items-center tw-items-stretch"
          key={`expand-${mainCategorySlug}/${categorySlug}`}
          onMouseEnter={() => handleMouseEnter('all')}
          onMouseLeave={handleMouseLeave}
        >
          <Link className="tw-w-full" href={`/${mainCategorySlug}/${categorySlug}`}>
            <Button
              variant="outline"
              className="tw-aspect-video !tw-h-full !tw-w-full tw-transition-all tw-duration-200 hover:tw-scale-[var(--scale-hover-outer)] hover:tw-opacity-100 hover:tw-shadow-[0_0_20px_2px_rgba(0,0,0,0.75)]"
            >
              View all
            </Button>
          </Link>
        </li>
      )}
    </ul>
  )
}

export type SubCategoriesGridProps = {
  mainCategorySlug: string
  categorySlug?: string
  className?: string
  categories?: Category[]
  subCategories?: SubCategory[]
}

export default function SubCategoriesGrid({
  mainCategorySlug,
  categorySlug,
  className,
  categories: initialCategories,
  subCategories: initialSubCategories,
}: SubCategoriesGridProps) {
  // Determine if we're viewing a multi-subcategory grid
  const isMultiSubCategory = !categorySlug

  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: async ({ signal }) => {
      const { data } = await client.get<Category[]>(`/categories`, {
        signal,
      })
      return data
    },
    enabled: isMultiSubCategory,
    initialData: initialCategories,
  })

  const { data: subCategories, isLoading: isLoadingSubCategories } = useQuery({
    queryKey: ['subcategories', mainCategorySlug, categorySlug],
    queryFn: async ({ signal }) => {
      const { data } = await client.get<SubCategory[]>(`/subcategories`, {
        params: {
          main_category: mainCategorySlug,
          category: categorySlug,
        },
        signal,
      })
      return data
    },
    initialData: initialSubCategories,
  })

  const isLoading = isLoadingCategories || isLoadingSubCategories

  // Group subcategories by category if we're viewing a multi-subcategory grid
  const groupedSubCategories = React.useMemo(() => {
    if (!subCategories) {
      return []
    }

    return subCategories.reduce(
      (acc, subCategory) => {
        const category = categories?.find((category) => category.slug === subCategory.categorySlug)

        const categoryName = category?.name ?? subCategory.categorySlug

        if (!acc[categoryName]) {
          acc[categoryName] = {
            icon: category?.icon,
            slug: subCategory.categorySlug,
            subCategories: [],
          }
        }

        acc[categoryName].subCategories.push(subCategory)

        return acc
      },
      {} as Record<
        string,
        {
          icon?: string | null
          slug: string
          subCategories: SubCategory[]
        }
      >
    )
  }, [categories, subCategories])

  // --- Handle hover state ---
  const [hoveredListSlug, setHoveredListSlug] = React.useState<string | null>(null)

  const onHoverList = useCallback((slug: string | null) => {
    setHoveredListSlug(slug)
  }, [])

  return (
    <>
      {isLoading && <SubCategoriesGridSkeleton className={className} />}

      {!isLoading && (
        <ul className={cn('subcategories-grid tw-grid tw-gap-2', className)} data-slot="subcategories-grid">
          {/*Iterate grouped subcategories*/}
          {Object.entries(groupedSubCategories).map(([categoryName, { slug, icon, subCategories }]) => (
            <li key={slug}>
              <HiddenSEOContent>
                <h3>{categoryName}</h3>
              </HiddenSEOContent>
              {isMultiSubCategory && (
                <Link
                  className="tw-group/title tw-inline-flex tw-items-center tw-space-x-2"
                  href={`/${mainCategorySlug}/${slug}`}
                >
                  {!!icon && <span style={{ fontSize: 24 }}>{icon}</span>}
                  <h4 className="heading_4 tw-py-2 !tw-font-normal tw-capitalize group-hover/title:tw-underline">
                    {categoryName}
                  </h4>
                </Link>
              )}
              <SubCategoriesList
                className={cn('tw-transition-opacity tw-duration-300 hover:!tw-opacity-100', {
                  'tw-opacity-65': !!hoveredListSlug && hoveredListSlug !== slug,
                })}
                mainCategorySlug={mainCategorySlug}
                categorySlug={slug}
                subCategories={subCategories}
                initiallyVisibleRows={isMultiSubCategory ? 2 : undefined}
                onHover={() => onHoverList(hoveredListSlug === slug ? null : slug)}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
